/**
 * Borrowing Entity & Guarantor DTOs
 *
 * Zod schemas and TypeScript types for multi-entity borrower relationships,
 * co-borrowers, and guarantor management.
 *
 * Issue #636 — Phase 37
 */

import { z } from 'zod';
import {
  BorrowingEntityStatusSchema,
  BorrowingEntityTypeSchema,
  CaseBorrowerRoleSchema,
  EntityOwnerRoleSchema,
  EntityOwnerTypeSchema,
  GuaranteeTypeSchema,
  GuarantorStatusSchema,
  GuarantorTypeSchema,
} from '../enums/borrowing-entity.enums';

// ============================================================================
// ADDRESS (reuse from loan-intake pattern)
// ============================================================================

const AddressSchema = z.object({
  street1: z.string().min(1),
  street2: z.string().optional(),
  city: z.string().min(1),
  state: z.string().min(2).max(2),
  postalCode: z.string().min(5),
  country: z.string().default('US'),
});

// ============================================================================
// BORROWING ENTITY
// ============================================================================

/** Schema for creating a borrowing entity */
export const CreateBorrowingEntitySchema = z.object({
  entityType: BorrowingEntityTypeSchema,
  legalName: z.string().min(1).max(200),
  dbaName: z.string().max(200).optional(),
  ein: z
    .string()
    .regex(/^\d{2}-?\d{7}$/, 'EIN must be 9 digits (with or without dash, e.g. 12-3456789)')
    .transform((val) => val.replace(/-/g, ''))
    .optional(),
  stateOfFormation: z.string().min(2).max(2).optional(),
  formationDate: z.string().datetime().optional(),
  registeredAgent: z.string().max(200).optional(),
  registeredAddress: AddressSchema.optional(),
  status: BorrowingEntityStatusSchema.optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});
export type CreateBorrowingEntityInput = z.infer<typeof CreateBorrowingEntitySchema>;

/** Schema for updating a borrowing entity */
export const UpdateBorrowingEntitySchema = CreateBorrowingEntitySchema.partial();
export type UpdateBorrowingEntityInput = z.infer<typeof UpdateBorrowingEntitySchema>;

/** Full borrowing entity response */
export const BorrowingEntitySchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  entityType: BorrowingEntityTypeSchema,
  legalName: z.string(),
  dbaName: z.string().nullable().optional(),
  ein: z.string().nullable().optional(),
  stateOfFormation: z.string().nullable().optional(),
  formationDate: z.string().nullable().optional(),
  registeredAgent: z.string().nullable().optional(),
  registeredAddress: AddressSchema.nullable().optional(),
  status: BorrowingEntityStatusSchema,
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type BorrowingEntityDto = z.infer<typeof BorrowingEntitySchema>;

// ============================================================================
// ENTITY OWNERSHIP
// ============================================================================

/** Schema for adding an owner to a borrowing entity */
export const CreateEntityOwnershipSchema = z.object({
  borrowingEntityId: z.string().uuid(),
  ownerType: EntityOwnerTypeSchema,
  /** Consumer ID — required when ownerType is 'individual' */
  individualConsumerId: z.string().uuid().optional(),
  /** Another BorrowingEntity ID — required when ownerType is 'entity' (nested ownership) */
  ownerEntityId: z.string().uuid().optional(),
  ownershipPercentage: z.number().min(0).max(100),
  role: EntityOwnerRoleSchema,
  signingAuthority: z.boolean().default(false),
  effectiveDate: z.string().datetime().optional(),
  endDate: z.string().datetime().optional(),
});
export type CreateEntityOwnershipInput = z.infer<typeof CreateEntityOwnershipSchema>;

/** Schema for updating entity ownership */
export const UpdateEntityOwnershipSchema = CreateEntityOwnershipSchema.partial().omit({
  borrowingEntityId: true,
});
export type UpdateEntityOwnershipInput = z.infer<typeof UpdateEntityOwnershipSchema>;

/** Full entity ownership response */
export const EntityOwnershipSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  borrowingEntityId: z.string().uuid(),
  ownerType: EntityOwnerTypeSchema,
  individualConsumerId: z.string().uuid().nullable().optional(),
  ownerEntityId: z.string().uuid().nullable().optional(),
  ownershipPercentage: z.number(),
  role: EntityOwnerRoleSchema,
  signingAuthority: z.boolean(),
  effectiveDate: z.string().nullable().optional(),
  endDate: z.string().nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  // Populated relations
  consumer: z.object({ id: z.string(), name: z.string(), email: z.string() }).optional(),
  ownerEntity: z.object({ id: z.string(), legalName: z.string(), entityType: z.string() }).optional(),
});
export type EntityOwnershipDto = z.infer<typeof EntityOwnershipSchema>;

// ============================================================================
// LOAN GUARANTOR
// ============================================================================

/** Schema for adding a guarantor to a loan/case */
export const CreateLoanGuarantorSchema = z.object({
  caseId: z.string().uuid(),
  guarantorType: GuarantorTypeSchema,
  /** Consumer ID — required for individual guarantors */
  consumerId: z.string().uuid().optional(),
  /** BorrowingEntity ID — required for entity guarantors */
  borrowingEntityId: z.string().uuid().optional(),
  guaranteeType: GuaranteeTypeSchema,
  guaranteeAmount: z.number().positive().optional(),
  guaranteePercentage: z.number().min(0).max(100).optional(),
  // Financial profile
  netWorth: z.number().optional(),
  liquidity: z.number().optional(),
  creditScore: z.number().int().min(300).max(850).optional(),
  existingGuaranteeExposure: z.number().optional(),
  // Status & dates
  status: GuarantorStatusSchema.optional(),
  effectiveDate: z.string().datetime().optional(),
  expirationDate: z.string().datetime().optional(),
  guaranteeAgreementDocId: z.string().uuid().optional(),
});
export type CreateLoanGuarantorInput = z.infer<typeof CreateLoanGuarantorSchema>;

/** Schema for updating a loan guarantor */
export const UpdateLoanGuarantorSchema = CreateLoanGuarantorSchema.partial().omit({
  caseId: true,
});
export type UpdateLoanGuarantorInput = z.infer<typeof UpdateLoanGuarantorSchema>;

/** Full loan guarantor response */
export const LoanGuarantorSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  caseId: z.string().uuid(),
  guarantorType: GuarantorTypeSchema,
  consumerId: z.string().uuid().nullable().optional(),
  borrowingEntityId: z.string().uuid().nullable().optional(),
  guaranteeType: GuaranteeTypeSchema,
  guaranteeAmount: z.number().nullable().optional(),
  guaranteePercentage: z.number().nullable().optional(),
  netWorth: z.number().nullable().optional(),
  liquidity: z.number().nullable().optional(),
  creditScore: z.number().nullable().optional(),
  existingGuaranteeExposure: z.number().nullable().optional(),
  status: GuarantorStatusSchema,
  effectiveDate: z.string().nullable().optional(),
  expirationDate: z.string().nullable().optional(),
  guaranteeAgreementDocId: z.string().uuid().nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  // Populated relations
  consumer: z.object({ id: z.string(), name: z.string(), email: z.string() }).optional(),
  borrowingEntity: z.object({ id: z.string(), legalName: z.string(), entityType: z.string() }).optional(),
});
export type LoanGuarantorDto = z.infer<typeof LoanGuarantorSchema>;

// ============================================================================
// CASE BORROWER (Junction: Case ↔ BorrowingEntity)
// ============================================================================

/** Schema for adding a borrowing entity to a case */
export const CreateCaseBorrowerSchema = z.object({
  caseId: z.string().uuid(),
  borrowingEntityId: z.string().uuid(),
  role: CaseBorrowerRoleSchema,
  ownershipInterest: z.number().min(0).max(100).optional(),
});
export type CreateCaseBorrowerInput = z.infer<typeof CreateCaseBorrowerSchema>;

/** Full case borrower response */
export const CaseBorrowerSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  caseId: z.string().uuid(),
  borrowingEntityId: z.string().uuid(),
  role: CaseBorrowerRoleSchema,
  ownershipInterest: z.number().nullable().optional(),
  addedDate: z.string().datetime(),
  removedDate: z.string().nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  // Populated relations
  borrowingEntity: BorrowingEntitySchema.optional(),
});
export type CaseBorrowerDto = z.infer<typeof CaseBorrowerSchema>;

// ============================================================================
// GUARANTOR EXPOSURE (Aggregate view)
// ============================================================================

/** Aggregate guarantee exposure for an individual across all loans */
export const GuarantorExposureSchema = z.object({
  consumerId: z.string().uuid().optional(),
  borrowingEntityId: z.string().uuid().optional(),
  name: z.string(),
  totalGuaranteeAmount: z.number(),
  activeGuaranteeCount: z.number().int(),
  guarantees: z.array(
    z.object({
      caseId: z.string().uuid(),
      caseNumber: z.string().optional(),
      guaranteeType: GuaranteeTypeSchema,
      guaranteeAmount: z.number().nullable(),
      status: GuarantorStatusSchema,
    }),
  ),
});
export type GuarantorExposureDto = z.infer<typeof GuarantorExposureSchema>;

// ============================================================================
// ENTITY OWNERSHIP TREE (Nested structure)
// ============================================================================

/** Recursive ownership tree node */
export interface OwnershipTreeNode {
  id: string;
  type: 'individual' | 'entity';
  name: string;
  ownershipPercentage: number;
  role: string;
  signingAuthority: boolean;
  children?: OwnershipTreeNode[];
}

/** Full entity ownership tree response */
export const EntityOwnershipTreeSchema = z.object({
  entity: BorrowingEntitySchema,
  owners: z.array(
    z.object({
      id: z.string(),
      type: z.enum(['individual', 'entity']),
      name: z.string(),
      ownershipPercentage: z.number(),
      role: z.string(),
      signingAuthority: z.boolean(),
    }),
  ),
  totalOwnershipPercentage: z.number(),
});
export type EntityOwnershipTreeDto = z.infer<typeof EntityOwnershipTreeSchema>;
